<template>
  <i-tree
    :items="items"
    v-slot="{item, hover, ...props}"
  >
    <!--
      @slot Tree Item
      @binding item {object} the item object
      @binding hover {boolean} the hover state of the current item
    -->
    <slot
      :item="item"
      :hover="hover"
      v-bind="props"
    />
  </i-tree>
</template>

<script>
import { Tree as TreeModel } from '@/utils/tree';
import Tree from './Tree.vue';
import TreeItem from './TreeItem.vue';

function hasChildren(item) {
  return item.children && item.children.length > 0;
}

/**
 * @requires ./Tree.vue
 * @requires ./TreeItem.vue
 */
export default {
  name: 'i-tree-view',
  components: {
    [Tree.name]: Tree,
    [TreeItem.name]: TreeItem,
  },
  props: {
    /**
      * Array of tree items representing a tree.
      * Each tree item should have a unique id.
      * An item might have an array of `children`
     */
    items: {
      type: Array,
      required: true,
    },
    /**
      * Array of item keys to control which items are expanded
     */
    expandedKeys: {
      type: Array,
      default: () => [],
    },
    /**
     * Key to control the currently selected item.
     * Clicking on a tree item will update the selected key.
     */
    selectedKey: {
      type: String,
    },
    expandable: {
      type: Function,
    },
    /**
     * Callback to dynamically load the children of an item
     */
    loadChildren: {
      type: Function,
    },
  },
  provide() {
    const {
      expandItem, collapseItem,
      toggleItem, isExpanded,
      selectItem, isSelected,
      isExpandable, status,
      isFocusable, navUp, navDown, navRight, navLeft,
    } = this;
    return {
      $treeContext: () => ({
        expandItem,
        collapseItem,
        toggleItem,
        isExpanded,
        selectItem,
        isSelected,
        isExpandable,
        status,
        isFocusable,
        navUp,
        navDown,
        navRight,
        navLeft,
      }),
    };
  },
  data() {
    return {
      statusKeys: {},
      // the current tree item that can receive focus
      focusableKey: this.selectedKey || this.items[0].id,
    };
  },
  methods: {
    _resolveChildren(item) {
      if (item.children) return item.children;
      if (this.isExpandable(item) && typeof this.loadChildren === 'function') {
        return this.loadChildren(item);
      }
      return [];
    },
    async resolveChildren(item) {
      this.statusKeys = {
        ...this.statusKeys,
        [item.id]: 'loading',
      };
      const children = await this._resolveChildren(item);
      this.statusKeys = {
        ...this.statusKeys,
        [item.id]: 'resolved',
      };
      return children;
    },
    isExpanded(item) {
      return this.expandedKeys.includes(item.id);
    },
    collapseItem(item) {
      const { $emit, expandedKeys } = this;
      $emit('update:expandedKeys', expandedKeys.filter((key) => key !== item.id));
    },
    async expandItem(item, opts = {}) {
      if (!this.isExpandable(item)) return;

      const children = await this.resolveChildren(item);
      // TODO: refactor the following state mutation(!)
      // eslint-disable-next-line
      item.children = children;

      const descendants = opts.expandDescendants ? TreeModel.findDescendants(item).filter(hasChildren).map((i) => i.id) : [];
      this.$emit('update:expandedKeys', [...new Set([...this.expandedKeys, item.id, ...descendants])]);
    },
    async toggleItem(item, opts) {
      if (this.isExpanded(item)) {
        this.collapseItem(item);
      } else {
        this.expandItem(item, opts);
      }
    },
    selectItem(item) {
      this.$emit('update:selectedKey', item.id);
      this.setFocusable(item);
    },
    isSelected(item) {
      return this.selectedKey === item.id;
    },
    isExpandable(item) {
      return (
        Boolean(item.children && item.children.length > 0)
        || (typeof this.expandable === 'function' && this.expandable(item))
      );
    },
    isFocusable(item) {
      return this.focusableKey === item.id;
    },
    setFocusable(item) {
      if (!item) return;
      this.focusableKey = item.id;
    },
    navUp(focusedItem) {
      const { focusableItems, setFocusable } = this;
      const index = focusableItems.findIndex((item) => focusedItem.id === item.id);
      const prevItem = focusableItems[index - 1];
      setFocusable(prevItem);
    },
    navDown(focusedItem) {
      const { focusableItems, setFocusable } = this;
      const index = focusableItems.findIndex((item) => focusedItem.id === item.id);
      const nextItem = focusableItems[index + 1];
      setFocusable(nextItem);
    },
    navRight(focusedItem) {
      const { status, isExpanded, toggleItem, navDown } = this; // eslint-disable-line

      if (status(focusedItem) === 'loading') return;

      // When focus is on a open node, moves focus to the first child node.
      if (isExpanded(focusedItem)) {
        navDown(focusedItem);
      }
      // When focus is on a closed node, opens the node; focus does not move.
      if (!isExpanded(focusedItem)) {
        toggleItem(focusedItem);
      }
    },
    navLeft(focusedItem) {
      const { items, isExpanded, setFocusable, toggleItem } = this; // eslint-disable-line

      // When focus is on an open node, closes the node.
      if (isExpanded(focusedItem)) {
        toggleItem(focusedItem);
      }
      //  When focus is on a child node that is also either an end node or a closed node, moves focus to its parent node.
      if (!isExpanded(focusedItem)) {
        const parentItem = TreeModel.findParent(items, (i) => i.id === focusedItem.id);
        setFocusable(parentItem);
      }
    },
    status(item) {
      return this.statusKeys[item.id] || 'idle';
    },
  },
  computed: {
    /**
     * @return all visible tree items as an array
     */
    focusableItems() {
      const { items, isExpanded } = this;
      return TreeModel.flatten(items, isExpanded);
    },
  },
};

</script>
